import type { ProviderInstrumentsModel } from '.';
import {
    cancelRemove as cancelRemoveAction,
    closeDialog as closeDialogAction,
    EMPTY_INSTRUMENT_DRAFT,
    failRemove as failRemoveAction,
    failSave as failSaveAction,
    finishRemove as finishRemoveAction,
    finishSave as finishSaveAction,
    initialProviderInstrumentsState,
    instrumentRows,
    normalizeInstrumentSymbol,
    openDialog as openDialogAction,
    parseInstrumentDraft,
    patchDraft as patchDraftAction,
    providerInstrumentsReducer,
    requestRemove as requestRemoveAction,
    setDialogStep as setDialogStepAction,
    setProvider as setProviderAction,
    startRemove as startRemoveAction,
    startSave as startSaveAction,
} from './state';
import type {
    InstrumentDialogState,
    InstrumentDraft,
    PendingInstrumentRemovalState,
    ProviderInstrumentRow,
    ProviderInstrumentsAction,
    ProviderInstrumentsState,
} from './state';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { t } from 'i18next';
import type { ProviderInstruments } from 'src/admin/LAB/contracts/providers';
import { log } from 'src/admin/LAB/log';
import { inject, injectable } from 'src/features/ioc';
import { getProviderUpdateErrorMessage } from 'src/hooks/admin/LAB/providerUpdateError';
import {
    addProviderInstrument,
    invalidateProvidersQuery,
    removeProviderInstrument,
    setCachedProviderInstruments,
} from 'src/queries/labProviders';
import type { ReactBindings } from 'src/types/bindings';

@injectable()
export class ProviderInstrumentsModelImpl implements ProviderInstrumentsModel {
    readonly #state = signal<ProviderInstrumentsState>(initialProviderInstrumentsState);
    /** Set on disposal so async write continuations bail out. */
    readonly #abort = new AbortController();
    #removalToken = 0;
    #saveToken = 0;
    readonly #queryClient: ReactBindings['QueryClient'];
    readonly #store: ReactBindings['Store'];

    readonly rows: ReadonlySignal<readonly ProviderInstrumentRow[]>;
    readonly dialog: ReadonlySignal<InstrumentDialogState | null>;
    readonly canReview: ReadonlySignal<boolean>;
    readonly replacesExisting: ReadonlySignal<ProviderInstrumentRow | null>;
    readonly pendingRemoval: ReadonlySignal<PendingInstrumentRemovalState | null>;

    constructor(
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('Store') store: ReactBindings['Store'],
    ) {
        this.#queryClient = queryClient;
        this.#store = store;
        // Derive from an intermediate signal so dialog edits do not re-sort an unchanged map.
        const instruments = computed(() => this.#state.value.instruments);
        this.rows = computed(() => instrumentRows(instruments.value));
        this.dialog = computed(() => this.#state.value.dialog);
        this.pendingRemoval = computed(() => this.#state.value.pendingRemoval);
        this.canReview = computed(() => {
            const dialog = this.#state.value.dialog;
            return dialog !== null && parseInstrumentDraft(dialog) !== null;
        });
        this.replacesExisting = computed(() => {
            const { dialog, instruments } = this.#state.value;
            if (dialog === null || dialog.mode !== 'add') return null;
            const symbol = normalizeInstrumentSymbol(dialog.draft.symbol);
            const entry = symbol ? instruments[symbol] : undefined;
            return entry ? { ...entry, symbol } : null;
        });
    }

    #dispatch(action: ProviderInstrumentsAction): void {
        this.#state.value = providerInstrumentsReducer(this.#state.value, action);
    }

    setProvider(providerId: string | null, instruments: ProviderInstruments | undefined): void {
        this.#dispatch(setProviderAction(providerId, instruments ?? {}));
    }

    openAdd(): void {
        this.#dispatch(
            openDialogAction({
                mode: 'add',
                original: null,
                draft: EMPTY_INSTRUMENT_DRAFT,
                step: 'form',
                isSaving: false,
                saveToken: null,
                error: null,
            }),
        );
    }

    openEdit(symbol: string): void {
        const entry = this.#state.value.instruments[symbol];
        if (!entry) return;
        this.#dispatch(
            openDialogAction({
                mode: 'edit',
                original: { ...entry, symbol },
                draft: { symbol, reason: entry.reason },
                step: 'form',
                isSaving: false,
                saveToken: null,
                error: null,
            }),
        );
    }

    closeDialog(): void {
        this.#dispatch(closeDialogAction());
    }

    updateDraft(patch: Partial<InstrumentDraft>): void {
        this.#dispatch(patchDraftAction(patch));
    }

    reviewDraft(): void {
        if (!this.canReview.value) return;
        this.#dispatch(setDialogStepAction('confirm'));
    }

    editDraft(): void {
        this.#dispatch(setDialogStepAction('form'));
    }

    async confirmSave(): Promise<void> {
        const { providerId, dialog } = this.#state.value;
        if (providerId === null || dialog === null || dialog.isSaving) return;
        const request = parseInstrumentDraft(dialog);
        if (request === null) return;
        const token = ++this.#saveToken;
        this.#dispatch(startSaveAction(token));
        let instruments: ProviderInstruments;
        try {
            instruments = await addProviderInstrument(providerId, request);
        } catch (error) {
            log.error(`Suspending symbol ${request.symbol} on provider ${providerId} failed`, {
                error,
                providerId,
                symbol: request.symbol,
            });
            if (this.#abort.signal.aborted || this.#state.value.providerId !== providerId) return;
            this.#dispatch(
                failSaveAction(
                    token,
                    getProviderUpdateErrorMessage(error) ??
                        t(
                            'admin::lab::provider-configuration::instruments::save-error',
                            'Saving the symbol settings failed. Please try again.',
                        ),
                ),
            );
            return;
        }
        // The server applied the write, so the shared cache is patched even if this model is gone.
        this.#syncProviderCache(providerId, instruments);
        if (this.#abort.signal.aborted || this.#state.value.providerId !== providerId) return;
        this.#dispatch(finishSaveAction(token, instruments));
        this.#toast(
            dialog.mode === 'edit'
                ? t('admin::lab::provider-configuration::instruments::edit-success', {
                      defaultValue: 'Updated the suspension of {{symbol}}.',
                      symbol: request.symbol,
                  })
                : t('admin::lab::provider-configuration::instruments::add-success', {
                      defaultValue: 'Suspended {{symbol}} on this provider.',
                      symbol: request.symbol,
                  }),
        );
    }

    requestRemove(symbol: string): void {
        const entry = this.#state.value.instruments[symbol];
        if (!entry) return;
        this.#dispatch(requestRemoveAction({ ...entry, symbol }, ++this.#removalToken));
    }

    cancelRemove(): void {
        this.#dispatch(cancelRemoveAction());
    }

    async confirmRemove(): Promise<void> {
        const { providerId, pendingRemoval } = this.#state.value;
        if (providerId === null || pendingRemoval === null || pendingRemoval.isRemoving) return;
        const { token, entry } = pendingRemoval;
        this.#dispatch(startRemoveAction());
        let instruments: ProviderInstruments;
        try {
            instruments = await removeProviderInstrument(providerId, {
                symbol: entry.symbol,
            });
        } catch (error) {
            log.error(`Removing symbol ${entry.symbol} from provider ${providerId} failed`, {
                error,
                providerId,
                symbol: entry.symbol,
            });
            if (this.#abort.signal.aborted) return;
            this.#dispatch(
                failRemoveAction(
                    token,
                    getProviderUpdateErrorMessage(error) ??
                        t(
                            'admin::lab::provider-configuration::instruments::remove-error',
                            'Removing the symbol failed. Please try again.',
                        ),
                ),
            );
            return;
        }
        this.#syncProviderCache(providerId, instruments);
        if (this.#abort.signal.aborted || this.#state.value.providerId !== providerId) return;
        this.#dispatch(finishRemoveAction(token, instruments));
        this.#toast(
            t('admin::lab::provider-configuration::instruments::remove-success', {
                defaultValue: 'Removed the suspension of {{symbol}}.',
                symbol: entry.symbol,
            }),
        );
    }

    #toast(message: string): void {
        this.#store.dispatch({
            type: 'toast::enqueue',
            toast: { message, group: 'default', variant: 'success' },
        });
    }

    #syncProviderCache(providerId: string, instruments: ProviderInstruments): void {
        setCachedProviderInstruments(this.#queryClient, providerId, instruments);
        void invalidateProvidersQuery(this.#queryClient);
    }

    [Symbol.dispose](): void {
        this.#abort.abort();
    }
}
